import { useState, useEffect } from "react";
import { useSEO } from "@/hooks/use-seo";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, CheckCircle2, Eye, Bot, Radar } from "lucide-react";
import { Link, useLocation } from "wouter";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import logoWrkt from "@assets/Wrkt_-_PNG_1771840403931.png";
import Footer from "@/components/Footer";
import ExpertContact from "@/components/ExpertContact";
import logoW from "@assets/W_1771840384312.webp";
import { HeroBackground } from "@/components/HeroBackground";
import robinImg from "@assets/1678270794100_1771852265016.jpeg";
import signalReport from "@assets/signal-report.webp";
import basImg from "@assets/Scherm­afbeelding_2026-02-24_om_11.19.40_1771938785535.png";
import juniorImg from "@assets/eIZFVQ4EPdTXUNb3K07jRo1oZVyn0pulPzWwcKV6_(1)_1771928334746.jpg";
import shayedImg from "@assets/shayed_sarda_1771928393746.png";
import stephanieImg from "@assets/stephanie-bergman.webp";
import bgZevy from "@assets/Zevy-wasparfum-alle-geuren_1771929314521.webp";
import bgVedder from "@assets/anne-en-esther-vedder-anne-vooral-jonge-meiden-denken-dat_1771929314522.avif";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export default function About() {
  const [scrolled, setScrolled] = useState(false);
  const [, setLocation] = useLocation();
  const { t, toggleLanguage, language, localePath } = useLanguage();
  const nl = language === 'nl';

  useSEO({
    title: nl
      ? 'Over Ons | Wrkt Digital - AI Marketing Specialisten Amsterdam'
      : 'About Us | Wrkt Digital - AI Marketing Specialists Amsterdam',
    description: nl
      ? 'Maak kennis met Wrkt Digital: experts in het strategisch groeien van D2C brands. 15+ jaar performance-expertise, eigen AI-tooling (Wrkt Signal) en een senior team in Amsterdam. Je werkt direct met je strateeg.'
      : 'Meet Wrkt Digital: experts in strategically growing D2C brands. 15+ years of performance expertise, proprietary AI tooling (Wrkt Signal) and a senior team in Amsterdam. You work directly with your strategist.',
    path: '/about',
    lang: nl ? 'nl' : 'en',
    keywords: nl
      ? 'over wrkt digital, marketing team amsterdam, AI marketing specialisten, digital marketing bureau amsterdam team, e-commerce marketing experts, performance marketing team, online marketing specialisten Nederland, growth agency team, D2C growth specialisten'
      : 'about wrkt digital, marketing team amsterdam, AI marketing specialists, digital marketing agency amsterdam team, e-commerce marketing experts, performance marketing team, online marketing specialists Netherlands, growth agency team, D2C growth specialists',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const teamMembers = [
    { name: 'Bas Lens', role: 'Founder', img: basImg },
    { name: 'Junior Panders', role: 'Development Director', img: juniorImg },
    { name: 'Robin de Wildt', role: 'Digital Growth Expert', img: robinImg },
    { name: 'Shayed Sarda', role: 'Head of Digital', img: shayedImg },
    { name: 'Stephanie Bergman', role: 'Senior Content & CRM Marketeer', img: stephanieImg },
  ];

  return (
    <div className="min-h-screen bg-[#0A1F14] text-white font-sans selection:bg-[#4CEAAC] selection:text-[#0A1F14]" role="main">
      <style>{`
        @keyframes color-pulse-shift {
          0% { background-color: rgba(141, 24, 43, 0.5); transform: scale(1); opacity: 0.5; }
          50% { background-color: rgba(76, 234, 172, 0.2); transform: scale(1.04); opacity: 0.35; }
          100% { background-color: rgba(141, 24, 43, 0.5); transform: scale(1); opacity: 0.5; }
        }
      `}</style>

      <Navbar activePage="about" />


      {/* ─── Hero - Wie we zijn ─── */}
      <section className="relative min-h-[70vh] flex items-center pt-24 pb-16 overflow-hidden bg-grad-hero has-grain breathing">

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <p className="text-[#4CEAAC] text-sm font-semibold uppercase tracking-widest mb-4">
              {nl ? 'Over Wrkt Digital' : 'About Wrkt Digital'}
            </p>
            <h1 className="text-5xl md:text-[68px] font-display font-bold leading-[1.04] tracking-[-0.03em] mb-6">
              {nl ? 'Experts in het ' : 'Experts in '}<span className="text-[#4CEAAC] [text-shadow:0_0_20px_rgba(76,234,172,0.4)]">{nl ? 'strategisch groeien' : 'strategically growing'}</span>{' '}
              {nl ? 'van D2C brands' : 'D2C brands'}
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-4">
              {nl
                ? 'Wrkt Digital is opgericht met één overtuiging: groei hoort voorspelbaar en meetbaar te zijn. Tien jaar Wrkt, 15+ jaar performance-expertise per strateeg, eigen AI-tooling en een senior team dat e-commerce door en door kent.'
                : 'Wrkt Digital was founded on one conviction: growth should be predictable and measurable. Ten years of Wrkt, 15+ years of performance expertise per strategist, proprietary AI tooling and a senior team that knows e-commerce inside out.'}
            </p>
            <p className="text-white/50 leading-relaxed">
              {nl
                ? 'Je werkt direct met je strateeg, volgt je campagnes dagelijks in je eigen dashboard en weet elke maand precies wat de volgende stap is.'
                : 'You work directly with your strategist, follow your campaigns daily in your own dashboard, and know exactly what the next step is every month.'}
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }} className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 pt-16 border-t border-white/10">
            {[
              { val: '2017', label: nl ? 'OPGERICHT' : 'FOUNDED' },
              { val: '15+', label: nl ? 'JAAR ERVARING' : 'YEARS OF EXPERIENCE' },
              { val: '100+', label: nl ? 'D2C-MERKEN BEGELEID' : 'D2C BRANDS GUIDED' },
              { val: 'MAX 5', label: nl ? 'KLANTEN PER STRATEEG' : 'CLIENTS PER STRATEGIST' },
            ].map((stat, i) => (
              <div key={i} className="space-y-1">
                <span className="text-3xl md:text-4xl font-display font-bold text-[#4CEAAC] italic [text-shadow:0_0_15px_rgba(76,234,172,0.3)]">{stat.val}</span>
                <p className="text-xs text-white/40 font-semibold tracking-wider">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── AI Tooling - Onze geheime wapens ─── */}
      <section className="py-24 bg-[#06140D] border-y border-white/5 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={bgZevy} alt="" aria-hidden="true" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-10" />
          <div className="absolute inset-0 bg-[#06140D]/90" />
        </div>
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[radial-gradient(ellipse_at_top_right,rgba(37,108,67,0.15),transparent_70%)] pointer-events-none" />
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="text-center mb-6">
            <p className="text-[#4CEAAC] text-sm font-semibold uppercase tracking-widest mb-3">
              {nl ? 'Onze technologie' : 'Our technology'}
            </p>
            <h2 className="text-4xl md:text-[56px] font-display font-bold leading-[1.04] tracking-[-0.03em] mb-4">
              {nl ? 'Eigen ' : 'Proprietary '}<span className="text-[#4CEAAC]">AI-tooling</span>{nl ? '. Elke dag aan het werk.' : '. Working every day.'}
            </h2>
            <div className="w-12 h-1 bg-[#4CEAAC] mx-auto mb-6" />
            <p className="text-white/50 max-w-2xl mx-auto leading-relaxed">
              {nl
                ? 'Onze eigen AI-setup kijkt 24/7 mee met je markt en je campagnes. Zo zien we meer en schakelen we sneller.'
                : 'Our own AI setup watches your market and campaigns 24/7. We see more and act faster.'}
            </p>
          </motion.div>

          <div className="grid gap-10 mt-12 lg:grid-cols-2 items-center">
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#06140D] shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
              <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.04] px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-white/15"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-white/15"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-white/15"></span>
                <span className="mx-auto flex items-center gap-1.5 rounded-md bg-white/[0.06] px-3 py-1 text-[11px] text-white/50">
                  <Radar className="h-3 w-3 text-[#4CEAAC]" /> signal.wrkt.nl
                </span>
              </div>
              <div className="relative h-[520px] overflow-hidden">
                <img src={signalReport} alt={nl ? 'Wrkt Signal rapport: creative-analyse van een actieve Meta-catalogus' : 'Wrkt Signal report: creative analysis of an active Meta catalog'} loading="eager" className="signal-scroll w-full" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#06140D] to-transparent"></div>
              </div>
            </div>
          <div className="grid gap-6">
            {[
              {
                icon: Eye,
                title: 'Creative Intelligence',
                items: nl
                  ? ['Ad library scanning (META, Google, TikTok)', 'Creative gap analyse', 'Trend detectie & format benchmarks', 'Competitor monitoring', 'Content performance scoring']
                  : ['Ad library scanning (META, Google, TikTok)', 'Creative gap analysis', 'Trend detection & format benchmarks', 'Competitor monitoring', 'Content performance scoring'],
              },
              {
                icon: Bot,
                title: nl ? 'Campagne-optimalisatie' : 'Campaign optimization',
                items: nl
                  ? ['Dagelijkse optimalisatie op rendement', 'Cross-channel budgetallocatie', 'Realtime performance-alerts', 'Doorlopende A/B-tests', 'Live dashboards via GA4 en Databox']
                  : ['Daily optimization on return', 'Cross-channel budget allocation', 'Realtime performance alerts', 'Continuous A/B testing', 'Live dashboards via GA4 and Databox'],
              },
            ].map((weapon, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.15 }}>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm relative group transition-all duration-500 hover:bg-white/[0.07] hover:border-[#4CEAAC]/30 overflow-hidden h-full">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(76,234,172,0.05)_0%,transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  <div className="flex items-center gap-3 mb-6 relative z-10">
                    <div className="w-12 h-12 rounded-xl bg-[#256C43]/50 border border-[#4CEAAC]/20 flex items-center justify-center">
                      <weapon.icon className="w-5 h-5 text-[#4CEAAC]" />
                    </div>
                    <h3 className="text-xl font-display font-bold">{weapon.title}</h3>
                  </div>
                  <ul className="space-y-3.5 relative z-10">
                    {weapon.items.map((item, j) => (
                      <li key={j} className="flex items-center gap-3 text-white/60 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#4CEAAC] shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
          </div>
        </div>
      </section>

      {/* ─── Vergelijking - Waarom wij ─── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full blur-[100px] pointer-events-none mix-blend-screen" style={{ animation: 'color-pulse-shift 20s ease-in-out infinite', animationDelay: '10s' }} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(76,234,172,0.04),transparent_60%)]" />
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="text-center mb-6">
            <p className="text-[#4CEAAC] text-sm font-semibold uppercase tracking-widest mb-3">
              {nl ? 'Zo werken wij' : 'How we work'}
            </p>
            <h2 className="text-4xl md:text-[56px] font-display font-bold leading-[1.04] tracking-[-0.03em] mb-4">
              {nl ? 'Proactief en ' : 'Proactive and '}<span className="text-[#4CEAAC]">{nl ? 'datagedreven' : 'data-driven'}</span>.
            </h2>
            <div className="w-12 h-1 bg-[#4CEAAC] mx-auto mb-6" />
            <p className="text-white/50 max-w-2xl mx-auto leading-relaxed">
              {nl
                ? 'Zes gewoontes die bepalen hoe we elke dag aan jouw merk werken.'
                : 'Six habits that shape how we work on your brand every day.'}
            </p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.15 }} className="mt-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(nl ? [
                { t: 'Analyse', d: 'Onze AI leest je accounts realtime en spot kansen voordat je concurrent ze ziet.' },
                { t: 'Creatives', d: 'Elke hook, visual en format is gebouwd op wat de data bewijst.' },
                { t: 'Rapportage', d: 'Eén live dashboard met 24/7 inzicht in wat je adspend oplevert.' },
                { t: 'Optimalisatie', d: 'Dagelijkse bijsturing op rendement: budget, biedingen en creatives.' },
                { t: 'Trends', d: 'We scannen de ad libraries van jouw markt en zien elke verschuiving direct.' },
                { t: 'Schaal', d: 'Slimme tooling doet het zware werk, ons senior team houdt de regie.' },
              ] : [
                { t: 'Analysis', d: 'Our AI reads your accounts in realtime and spots opportunities before your competitors do.' },
                { t: 'Creatives', d: 'Every hook, visual and format is built on what the data proves.' },
                { t: 'Reporting', d: 'One live dashboard with 24/7 insight into what your ad spend delivers.' },
                { t: 'Optimization', d: 'Daily adjustments steered on return: budgets, bids and creatives.' },
                { t: 'Trends', d: 'We scan the ad libraries in your market and see every shift as it happens.' },
                { t: 'Scale', d: 'Smart tooling does the heavy lifting, our senior team stays in control.' },
              ]).map((item, i) => (
                <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-6 hover:border-[#4CEAAC]/30 transition-colors">
                  <div className="flex items-center gap-2.5 mb-2">
                    <span className="w-2 h-2 rounded-full bg-[#4CEAAC] shrink-0" />
                    <h3 className="font-display font-bold">{item.t}</h3>
                  </div>
                  <p className="text-sm text-white/55 leading-relaxed">{item.d}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── Strategisch groeipartner ─── */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="text-center mb-12">
            <h3 className="text-[#4CEAAC] text-xs font-semibold tracking-[0.18em] uppercase mb-4">
              {nl ? 'Strategisch groeipartner' : 'Strategic growth partner'}
            </h3>
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-6 leading-tight">
              {nl ? <>Geen bureau op afstand, maar een <span className="text-[#4CEAAC]">groeipartner</span>.</> : <>Not an agency at a distance, but a <span className="text-[#4CEAAC]">growth partner</span>.</>}
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto leading-relaxed">
              {nl
                ? 'We voeren niet alleen campagnes uit. We denken mee over forecasts, marges, assortiment en internationale expansie: elk kwartaal een plan, elke maand bijgestuurd op cijfers. Uitsluitend voor D2C e-commerce, met diepe kennis van fashion & sieraden, beauty & verzorging, home & living en food & health.'
                : 'We do more than run campaigns. We think along on forecasts, margins, assortment and international expansion: a plan every quarter, adjusted monthly based on the numbers. Exclusively for D2C e-commerce, with deep knowledge of fashion & jewelry, beauty & care, home & living and food & health.'}
            </p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.1 }} className="grid grid-cols-3 gap-6 max-w-3xl mx-auto mb-14 text-center">
            {[
              { val: '25', unit: nl ? 'MND' : 'MO', label: nl ? 'GEMIDDELDE KLANTRELATIE' : 'AVERAGE CLIENT RELATIONSHIP' },
              { val: '5', unit: nl ? 'JAAR' : 'YRS', label: nl ? 'LANGSTE SAMENWERKING' : 'LONGEST PARTNERSHIP' },
              { val: '100', unit: '%', label: nl ? 'FOCUS OP D2C E-COMMERCE' : 'FOCUS ON D2C E-COMMERCE' },
            ].map((st, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <div className="font-display font-bold text-3xl md:text-4xl text-[#4CEAAC]">{st.val}<span className="text-lg md:text-xl ml-1">{st.unit}</span></div>
                <div className="text-[10px] md:text-[11px] font-semibold tracking-widest text-white/40 mt-2">{st.label}</div>
              </div>
            ))}
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.7, delay: 0.15 }} className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {(nl ? [
              { q: 'Ze zijn niet alleen sterk in het opschalen van een start-up: ze gaan verder dan adverteren alleen, met forecasts, breed strategisch advies en een proactieve drive voor het beste resultaat.', name: 'Anne & Esther Vedder', role: 'Founders Feather Women & Vedder & Vedder', img: '/cases/avatars/avatar-vedder.jpg' },
              { q: 'Vanaf het eerste gesprek was duidelijk dat ze zowel de creatieve als de commerci\u00eble kant van het schalen van een premium merk begrijpen.', name: 'Alrik Kielman', role: 'Founder Moddern', img: '/cases/avatars/avatar-alrik.jpg' },
              { q: 'De expertise van Wrkt Digital helpt MIMI ET TOI naar het volgende niveau.', name: 'Mimi van Dijk', role: 'Founder MIMI ET TOI', img: '/cases/avatars/avatar-mimi.jpg' },
            ] : [
              { q: "They're not only good at scaling a start-up: they go beyond advertising alone, with forecasts, broad strategic advice and a proactive drive for the best results.", name: 'Anne & Esther Vedder', role: 'Founders Feather Women & Vedder & Vedder', img: '/cases/avatars/avatar-vedder.jpg' },
              { q: 'From the first call, it was clear they understand both the creative and the commercial side of scaling a premium brand.', name: 'Alrik Kielman', role: 'Founder Moddern', img: '/cases/avatars/avatar-alrik.jpg' },
              { q: "Wrkt Digital's expertise is helping MIMI ET TOI to the next level.", name: 'Mimi van Dijk', role: 'Founder MIMI ET TOI', img: '/cases/avatars/avatar-mimi.jpg' },
            ]).map((item, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 flex flex-col">
                <p className="text-sm text-white/75 leading-relaxed italic flex-1">“{item.q}”</p>
                <div className="flex items-center gap-3 mt-6 pt-5 border-t border-white/5">
                  <img src={item.img} alt={item.name} loading="lazy" decoding="async" className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-semibold">{item.name}</div>
                    <div className="text-xs text-white/40">{item.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ─── Team ─── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={bgVedder} alt="" aria-hidden="true" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-[#0A1F14]/85" />
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl opacity-[0.03] pointer-events-none z-0">
          <img src={logoW} alt="" aria-hidden="true" loading="lazy" decoding="async" className="w-full h-auto" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(76,234,172,0.05),transparent_60%)]" />
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="text-center mb-16">
            <p className="text-[#4CEAAC] text-sm font-semibold uppercase tracking-widest mb-3">
              {nl ? 'De mensen achter Wrkt' : 'The people behind Wrkt'}
            </p>
            <h2 className="text-4xl md:text-[56px] font-display font-bold leading-[1.04] tracking-[-0.03em] mb-4">
              {nl ? 'Ons ' : 'Our '}<span className="text-[#4CEAAC] [text-shadow:0_0_10px_rgba(76,234,172,0.6)]">team</span>
            </h2>
            <div className="w-12 h-1 bg-[#4CEAAC] mx-auto mb-6" />
            <p className="text-white/50 max-w-2xl mx-auto leading-relaxed">{t('about_team_desc')}</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {teamMembers.map((member, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.5, delay: 0.08 * i }}>
                <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0D2918] group hover:border-[#4CEAAC]/30 transition-colors">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={member.img}
                      alt={`${member.name}, ${member.role} bij Wrkt Digital`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover object-top transition-all duration-500 group-hover:scale-105 grayscale group-hover:grayscale-0"
                      data-testid={`img-team-${i}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0D2918] via-transparent to-transparent" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-display font-bold mb-0.5">{member.name}</h3>
                    <p className="text-[#4CEAAC] text-xs font-semibold">{member.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Locatie ─── */}
      <section className="py-16 relative">
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="w-5 h-5 text-[#4CEAAC]" />
                  <h3 className="text-2xl font-display font-bold">{t('about_location_title')}</h3>
                </div>
                <p className="text-white/60 leading-relaxed mb-4">{t('about_location_desc')}</p>
                <p className="text-white/35 text-sm">Herengracht 372, 1016 CH Amsterdam</p>
              </div>
              <div className="h-64 md:h-auto min-h-[250px] bg-[#0A1F14] relative overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2435.9!2d4.8875!3d52.3685!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c609c6917f9b3b%3A0x5b2c7c5f0b8a0f0!2sHerengracht%20372%2C%201016%20CH%20Amsterdam!5e0!3m2!1snl!2snl!4v1700000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'invert(0.9) hue-rotate(180deg) saturate(0.3) brightness(0.7) contrast(1.2)', minHeight: '250px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Wrkt Digital - Herengracht 372, Amsterdam"
                ></iframe>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative bg-grad-duo has-grain py-[120px] px-6 md:px-10 overflow-hidden">
        <motion.div {...fadeUp} transition={{ duration: 0.7 }} className="relative max-w-[900px] mx-auto text-center">
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.2em] text-white/70">
            {nl
              ? 'Dit is wie we zijn. De eerste stap? Een gratis Growth Scan.'
              : 'This is who we are. The first step? A free Growth Scan.'}
          </p>
          <h2 className="font-display font-extrabold text-[clamp(40px,6.2vw,80px)] tracking-[-0.04em] leading-[0.98] mt-5">
            {nl ? 'Weten waar je ' : 'Know where your '}
            <span className="bg-[linear-gradient(92deg,#4BF4B9,#86F0B4)] bg-clip-text text-transparent">{nl ? 'groei' : 'growth'}</span>
            {nl ? ' zit?' : ' is?'}
          </h2>
          <p className="text-[19px] leading-relaxed text-white/[0.78] max-w-[48ch] mx-auto mt-6">
            {nl
              ? 'Vraag een gratis Growth Scan aan of plan direct een kennismaking. Vrijblijvend, met een concreet beeld van je grootste kansen.'
              : 'Request a free Growth Scan or book an intro call right away. No strings attached, with a concrete view of your biggest opportunities.'}
          </p>
          <div className="flex gap-3.5 mt-10 justify-center flex-wrap">
            <button onClick={() => setLocation(localePath('/scan'))} className="btn btn--gradient btn--lg" data-testid="button-scan-cta">
              {nl ? 'Start je gratis Growth Scan →' : 'Start your free Growth Scan →'}
            </button>
            <button onClick={() => setLocation(localePath('/contact'))} className="btn btn--lg btn--outline-inverse" data-testid="button-audit-cta">
              {nl ? 'Plan een kennismaking →' : 'Book an intro call →'}
            </button>
          </div>
        </motion.div>
      </section>

      <ExpertContact />

      <Footer />
    </div>
  );
}
